import { memo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { IconArrowsReduceDiagonalOutline18 as Collapse } from "nucleo-ui-outline-18"

import type { CopilotScope } from "@productlane/lib/src/ai"
import { IconButton, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as More } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { useCopilotState } from "@/lib/states/copilot-state"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadSidebarState } from "../sidebar"
import { useSidebarAiChatState } from "../threads/components/right-bar/ai-chat/sidebar-ai-chat-state"
import { collapsedChatTarget } from "./agent-route"

export const CHAT_PAGE_COPY = {
	more: "Chat options",
	archive: "Archive chat",
	unarchive: "Unarchive chat",
	archived: "Chat archived",
	unarchived: "Chat restored",
	archiveFailed: "Couldn't change this chat",
	collapse: "Open in side panel",
}

// The full-page chat's top-right actions: archive it, or move it back into a side panel.
export const ChatPageActions = memo(
	({
		scope,
		collapsible = true,
	}: {
		scope: CopilotScope
		// Off on a suggestion, which needs the full page to review.
		collapsible?: boolean
	}) => {
		const navigate = useNavigate()
		const zero = useZero()
		const sessionId = useSidebarAiChatState((s) => s.sessionId)
		const [chat] = useQuery(queries.myAssistantChat({ id: sessionId ?? "" }), {
			enabled: !!sessionId,
		})
		const archived = !!chat?.archivedAt

		const toggleArchive = () => {
			if (!sessionId) return
			mutateOk(
				zero.mutate(
					mutators.aiChatSessions.archive({
						id: sessionId,
						archived: !archived,
					}),
				),
			).then(
				() =>
					toast.success({
						title: archived
							? CHAT_PAGE_COPY.unarchived
							: CHAT_PAGE_COPY.archived,
					}),
				() => toast.error({ title: CHAT_PAGE_COPY.archiveFailed }),
			)
		}

		const collapse = () => {
			const target = collapsedChatTarget(scope)
			if (target.kind === "thread") {
				useThreadSidebarState.getState().setActiveTab("ai-chat")
			} else if (target.kind === "editor") {
				useCopilotState.getState().openWith(scope)
			} else {
				const inbox: CopilotScope = { kind: "workspace" }
				// The inbox panel reopens the chat it remembers, so it picks this one up.
				if (sessionId)
					useCopilotState.getState().setRememberedSessionId(inbox, sessionId)
				useCopilotState.getState().openWith(inbox)
			}
			void navigate({ href: target.path })
		}

		return (
			<div className="flex flex-none items-center gap-1">
				{sessionId && (
					<DropdownMenu
						className="w-[180px]"
						contentProps={{ align: "end" }}
						trigger={
							<div>
								<IconButton
									Icon={More}
									variant="ghost"
									aria-label={CHAT_PAGE_COPY.more}
								/>
							</div>
						}
					>
						<DropdownMenuItem onSelect={toggleArchive}>
							{archived ? CHAT_PAGE_COPY.unarchive : CHAT_PAGE_COPY.archive}
						</DropdownMenuItem>
					</DropdownMenu>
				)}
				{collapsible && (
					<Tooltip label={CHAT_PAGE_COPY.collapse} side="bottom">
						<IconButton
							Icon={Collapse}
							iconStrokeWidth={1.5}
							variant="ghost"
							aria-label={CHAT_PAGE_COPY.collapse}
							data-testid="chat-page-collapse"
							onClick={collapse}
						/>
					</Tooltip>
				)}
			</div>
		)
	},
)
